import { fetchFromApi } from "../helpers/fetchFromApi";
import { getUserAccessLevel } from "../helpers/featureAccess";
import XGoalsMigration from './XGoalsMigration';

import { assertLeagueBalance, leagueTeamIds } from "../helpers/leagueReassessment";
import LeagueReassessment from "./LeagueReassessment";
import WorkspaceStandingsTable from "./WorkspaceStandingsTable";
import LeagueChoice from "./LeagueChoice";
import {
  createWorkareaTheme,
  readWorkareaMode,
} from "../helpers/workareaTheme";
import LeagueComparisonMatrix from "./LeagueComparisonMatrix";
import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Alert,
  CircularProgress,
  ThemeProvider,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { tipsRequest } from "../helpers/tipsWorkflow";
import {
  canUseTeamWorkspace,
  comparableHistory,
  comparisonMetrics,
} from "../helpers/teamWorkspace";
import { futureSummary } from "../helpers/teamAnalysis";
import { prepareLeagueRowsForScope } from "../helpers/compactLeagueRows";
import TeamAnalysisDialog from "./TeamAnalysisDialog";
import { leagueParameterColumns } from "../helpers/teamLeagueColumns";
import "./TeamLeagueWorkspace.css";
const baseColumns = [
  "played",
  "won",
  "draw",
  "lost",
  "goals",
  "difference",
  "points",
];
export default function TeamLeagueWorkspace({
  exploration = false,
  user,
  leagues: suppliedLeagues = [],
  initialLeague = null,
  initialSeason = null,
  initialTeams = null,
  fixture = null,
  onClose,
  onStart,
  request = tipsRequest,
}) {
  const { i18n } = useTranslation(),
    en = i18n.language.startsWith("en"),
    t = (de, eng) => (en ? eng : de);
  const mode = readWorkareaMode();
  const [loadedLeagues,setLoadedLeagues]=useState([]);
  const [catalogLoading,setCatalogLoading]=useState(false);
  const [catalogError,setCatalogError]=useState(false);
  const [catalogCached,setCatalogCached]=useState(false);
  const [catalogRetry,setCatalogRetry]=useState(0);
  const leagues=suppliedLeagues.length?suppliedLeagues:loadedLeagues;
  const startLeague=initialLeague;
  const guideSession=useRef({});
  const [orientation,setOrientation]=useState(0);

  const allowed = canUseTeamWorkspace(user),
    [leagueId, setLeagueId] = useState(startLeague?.id || ""),
    [seasons, setSeasons] = useState([]),
    [seasonId, setSeasonId] = useState(
      (exploration?startLeague?.current_season_id:initialSeason || initialLeague?.current_season_id) || ""
    ),
    [data, setData] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [teamIds, setTeamIds] = useState(initialTeams),
    [sort, setSort] = useState("position"),
    [venue, setVenue] = useState("overall"),
    [columns, setColumns] = useState([
      ...baseColumns,
      "quotient",
      "goalAverage",
    ]),
    [source, setSource] = useState("real"),
    [recent, setRecent] = useState(0),
    [perGame, setPerGame] = useState(false),
    [refresh, setRefresh] = useState(0),
    [allSources, setAllSources] = useState(false),
    [showStatistics, setShowStatistics] = useState(false);
  const [columnSearch, setColumnSearch] = useState("");
  const generation = useRef(0),
    scroll = useRef(null),
    savedScroll = useRef(0);
  const league =
    leagues.find((l) => Number(l.id) === Number(leagueId)) || initialLeague;
  useEffect(()=>{
    if(!allowed || suppliedLeagues.length) return;
    let active=true;
    const controller=new AbortController();
    const timeout=setTimeout(()=>controller.abort(),30000);
    setCatalogLoading(true);setCatalogError(false);setCatalogCached(false);
    (async()=>{
      const rows=[];let page=1,more=true;
      while(more){
        const response=await fetchFromApi(`fetch-data-v3/leagues?include=country&select=id,name,country_id,type,sub_type,image_path&per_page=50&page=${page}`, {signal:controller.signal});
        const payload=await response.json();
        if(!response.ok || !Array.isArray(payload.data)) throw new Error(payload?.code || 'CATALOG_FAILED');
        if(active && payload.catalog_cache?.stale)setCatalogCached(true);
        rows.push(...payload.data);more=payload.pagination?.has_more===true;page++;
      }
      if(!rows.length)throw new Error('CATALOG_EMPTY');
      if(active)setLoadedLeagues([...new Map(rows.map(l=>[l.id,l])).values()]);
    })().catch(error=>{if(active)setCatalogError(error.message || 'CATALOG_FAILED');}).finally(()=>{clearTimeout(timeout);if(active)setCatalogLoading(false);});
    return()=>{active=false;clearTimeout(timeout);controller.abort();};
  },[allowed,suppliedLeagues.length,catalogRetry]);
  const fmt = (v, d = 2) =>
    Number.isFinite(v)
      ? v.toLocaleString(en ? "en-GB" : "de-DE", {
          minimumFractionDigits: d,
          maximumFractionDigits: d,
        })
      : "—";
  useEffect(() => {
    if (!allowed || !leagueId) return;
    let active = true;
    setError("");
    request("statistic-model", { mode: "catalog", league_id: Number(leagueId) })
      .then((r) => {
        if (active) {
          const rows = r.seasons || [];
          setSeasons(rows);
          setSeasonId(
            (current) =>
              current ||
              rows.find((s) => s.is_current)?.season_id ||
              rows[rows.length - 1]?.season_id ||
              ""
          );
        }
      })
      .catch(() => {
        if (active)
          setError(
            t(
              "Spielzeiten konnten nicht geladen werden.",
              "Could not load seasons."
            )
          );
      });
    return () => {
      active = false;
    };
  }, [allowed, leagueId, request]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!allowed || !leagueId || !seasonId) return;
    let active = true;
    const seq = ++generation.current;
    setBusy(true);
    setData(null);
    setError("");
    request("statistic-model", {
      mode: "league-workspace",
      league_id: Number(leagueId),
      season_id: Number(seasonId),
    })
      .then((r) => {
        if (active && seq === generation.current) setData(r);
      })
      .catch(() => {
        if (active)
          setError(
            t(
              "Ligaansicht konnte nicht geladen werden. Bestehende Daten bleiben unverändert.",
              "Could not load league view. Existing data are unchanged."
            )
          );
      })
      .finally(() => {
        if (active) setBusy(false);
      });
    return () => {
      active = false;
    };
  }, [allowed, leagueId, seasonId, request, refresh]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!teamIds && scroll.current)
      scroll.current.scrollTop = savedScroll.current;
  }, [teamIds]);
  if (!allowed)
    return (
      <Dialog open onClose={onClose}>
        <DialogTitle>
          {t("Zugriff nicht freigegeben", "Access not granted")}
        </DialogTitle>
        <Button onClick={onClose}>{t("Schließen", "Close")}</Button>
      </Dialog>
    );
  const extraCatalog = leagueParameterColumns(
    data,
    en,
    (key) =>
      i18n.t(
        "help.automation.business.statisticNames." +
          data?.state?.registry?.[key]?.title,
        { defaultValue: data?.state?.registry?.[key]?.title || key }
      ),
    { recent, venue }
  );
  const labels = {
    played: t("Spiele", "Played"),
    won: t("Siege", "Wins"),
    draw: t("Remis", "Draws"),
    lost: t("Niederlagen", "Losses"),
    goals: t("Tore", "Goals"),
    difference: t("Differenz", "Difference"),
    points: t("Punkte", "Points"),
    goalAverage: t("Torschnitt", "Goals/game"),
    quotient: t("Torquotient", "Goal ratio"),
    ...Object.fromEntries(extraCatalog.map((c) => [c.key, c.label])),
  };
  const sourceLabel = {
    real: t("Real erspielt", "Actual performance"),
    previous: t("Vorsaison · real erspielt", "Previous season · actual"),
    result: t("Ergebnismodell · erwartet", "Results model · expected"),
    statistics: t("Statistikmodell · erwartet", "Statistics model · expected"),
    market: t("Marktrückrechnung", "Market inversion"),
    should: "Should Goals",
  }[source];
  const statisticColumns = ["quotient", "goalAverage"];
  const visibleColumns = columns.filter(
    (k) => !statisticColumns.includes(k) && !k.includes(":")
  );
  const extraColumns = extraCatalog.filter((c) => columns.includes(c.key));
  const visibleMetrics = statisticColumns.filter((k) => columns.includes(k));
  const groups = [
    sourceLabel,
    t(
      "Gespeicherte Settings · Ergebnismodell",
      "Saved settings · Results model"
    ),
    t(
      "Gespeicherte Settings · Statistikmodell",
      "Saved settings · Statistics model"
    ),
  ];
  const columnClass = (k) =>
    ["played", "won", "draw", "lost"].includes(k)
      ? "tl-record-column"
      : k === "points"
      ? "tl-points"
      : k === "goals"
      ? "tl-goals"
      : "tl-number";
  const digits = (k) =>
    k === "played"
      ? 0
      : perGame || !["real", "previous"].includes(source)
      ? 2
      : 0;
  const actual = prepareLeagueRowsForScope(data?.actual_table, "actual", venue),
    stat = new Map((data?.ranking || []).map((r) => [r.team_id, r]));
  const ids = [
    ...new Set([
      ...actual.map((r) => r.teamId),
      ...Object.keys(data?.result_teams || {}).map(Number),
      ...stat.keys(),
      ...(data?.observations || []).flatMap((f) => [
        f.home_team_id,
        f.away_team_id,
      ]),
    ]),
  ];
  const rulesFor = (id) =>
    (data?.standing_rules || []).filter(
      (rule) =>
        rule.is_regular_stage !== false &&
        (rule.position != null
          ? Number(rule.position) ===
            actual.find((r) => r.teamId === id)?.position
          : Number(rule.team_id) === id)
    );
  const rows = ids
    .map((id) => {
      let r = actual.find((r) => r.teamId === id) || {
        teamId: id,
        name: data?.result_teams?.[id]?.team_name,
      };
      if (source !== "real" || recent || perGame) {
        const pool = comparableHistory(
          data?.observations || [],
          id,
          [source === "previous" ? "real" : source],
          { recent, venue: venue === "overall" ? "all" : venue }
        );
        const m = comparisonMetrics(pool.selected, id, source, perGame);
        r = {
          ...r,
          played: m.count,
          outcomeCount: m.outcomes?.count,
          won: m.wins,
          draw: m.draws,
          lost: m.losses,
          points: m.points,
          goalsScored: m.goalsFor,
          goalsAgainst: m.goalsAgainst,
          difference: m.goalDifference,
          goalAverage: m.goal_sum,
          quotient: m.quotient,
          coverage: `${m.count}/${pool.selected.length}`,
        };
        if (source === "previous") {
          const archive = data?.history?.teams?.find((t) => t.team_id === id)
            ?.sources?.[0];
          const a =
              venue === "overall"
                ? archive?.actual_totals
                : archive?.actual_scopes?.[venue],
            n = a?.played,
            d = perGame ? n : 1;
          const f = venue === "overall" ? a?.for : a?.goalsScored,
            g = venue === "overall" ? a?.against : a?.goalsAgainst;
          const divide = (v) => (Number.isFinite(v) && d > 0 ? v / d : null);
          r = {
            ...r,
            played: n,
            outcomeCount: n,
            won: divide(a?.won),
            draw: divide(a?.draw),
            lost: divide(a?.lost),
            points: divide(a?.points),
            goalsScored: divide(f),
            goalsAgainst: divide(g),
            difference:
              Number.isFinite(f) && Number.isFinite(g) ? divide(f - g) : null,
            goalAverage:
              n > 0 && Number.isFinite(f) && Number.isFinite(g)
                ? (f + g) / n
                : null,
            quotient: g > 0 && Number.isFinite(f) ? f / g : null,
            coverage: archive
              ? `${archive.league_name} · ${archive.season_name}`
              : t("Archiv fehlt", "Archive missing"),
          };
        }
      }
      return {
        ...r,
        result: futureSummary(data?.result_teams?.[id]),
        statistics: stat.get(id),
      };
    })
    .sort((a, b) => {
      if (sort === "position")
        return (
          (b.points ?? -Infinity) - (a.points ?? -Infinity) ||
          (b.difference ?? -Infinity) - (a.difference ?? -Infinity) ||
          (b.goalsScored ?? -Infinity) - (a.goalsScored ?? -Infinity) ||
          a.teamId - b.teamId
        );
      const v = (r) =>
        sort === "result"
          ? r.result?.total_strength
          : sort === "statistics"
          ? r.statistics?.total_strength
          : extraCatalog.find(c=>c.key===sort)?.value(r.teamId) ?? r[sort];
      const av = v(a),
        bv = v(b);
      return !Number.isFinite(av)
        ? 1
        : !Number.isFinite(bv)
        ? -1
        : sort === "position"
        ? av - bv
        : bv - av;
    });
  let normalization = null;
  if (data?.state && leagueTeamIds(data).length) {
    try { assertLeagueBalance(data.result_teams,data.state,leagueTeamIds(data)); normalization=true; }
    catch { normalization=false; }
  }
  const saveView = () => {
    try {
      localStorage.setItem(
        "betty:owner-team-workspace:view",
        JSON.stringify({
          columns,
          sort,
          venue,
          source,
          recent,
          perGame,
          allSources,
          showStatistics,
        })
      );
    } catch {
      setError(
        t(
          "Ansicht konnte auf diesem Gerät nicht gespeichert werden.",
          "Could not save view on this device."
        )
      );
    }
  };
  const loadView = () => {
    try {
      const v = JSON.parse(
        localStorage.getItem("betty:owner-team-workspace:view")
      );
      if (v) {
        setColumns([
          ...new Set([
            ...(v.columns || baseColumns).filter((k) => k in labels),
            ...(!("showStatistics" in v) ? statisticColumns : []),
          ]),
        ]);
        setShowStatistics(v.showStatistics === true);
        setSort(
          ["position", "result", "statistics", "quotient", ...baseColumns, ...extraCatalog.map(c=>c.key)].includes(v.sort)
            ? v.sort
            : "position"
        );
        setVenue(
          ["overall", "home", "away"].includes(v.venue) ? v.venue : "overall"
        );
        setSource(
          [
            "real",
            "result",
            "statistics",
            "market",
            "should",
            "previous",
          ].includes(v.source)
            ? v.source
            : "real"
        );
        setRecent([0, 4, 5, 6, 7, 8].includes(v.recent) ? v.recent : 0);
        setPerGame(v.perGame === true);
        setAllSources(v.allSources === true);
      }
    } catch {
      setError(
        t(
          "Gespeicherte Ansicht ist nicht verfügbar.",
          "Saved view unavailable."
        )
      );
    }
  };
  return (
    <ThemeProvider theme={createWorkareaTheme(mode)}>
      <Dialog
        open
        fullScreen
        onClose={onClose}
        PaperProps={{ className: `team-league-workspace tl-${mode}` }}
      >
        <DialogTitle>
          {exploration?t("Liga entdecken & Teams analysieren","Explore leagues & analyse teams"):t("Team- und Liga-Arbeitsbereich", "Team and league workspace")}
          {exploration&&<span> · {league?.name} · {data?.season?.name||seasons.find(s=>Number(s.season_id)===Number(seasonId))?.season_name||"—"}</span>}
          {exploration&&onStart&&<Button onClick={onStart}>{t("Start","Home")}</Button>}
          <Button onClick={onClose}>{t("Schließen", "Close")}</Button>
        </DialogTitle>
        <DialogContent ref={scroll}>
          {data?.preview_notice && (
            <Alert severity="info">
              {data.preview_notice[en ? "en" : "de"]}
            </Alert>
          )}
          {exploration&&data&&<section className="metric-guide-controls">
            <h2>{t("Liga erkunden","Explore the league")}</h2>
            <div>{[t("Reale Tabelle","Actual table"),t("Modelle vergleichen","Compare models"),t("Leistung entdecken","Explore performance"),t("Erwartung vergleichen","Compare expectations"),t("Team verstehen & anpassen","Understand & adjust a team")].map((label,i)=><Button key={i} variant={orientation===i?'contained':'outlined'} onClick={()=>{
              setOrientation(i);setVenue('overall');setRecent(0);setPerGame(false);setAllSources(false);setColumns([...baseColumns,'quotient','goalAverage']);setShowStatistics(i>0);setSource(i===2?'should':i===3?'result':'real');setSort(i===1?'result':'position');
            }}>{i+1}. {label}</Button>)}</div>
            <p>{[
              t("Die reale Tabelle zeigt Punkte und Tore. Wähle eine Mannschaft für ihre Analyse. Die Liga kannst du unten jederzeit wechseln.","The actual table shows points and goals. Choose a team to analyse it. You can change leagues below."),
              t("Gespeicherte Spielstärke und Torniveau beider Modelle stehen neben der realen Bilanz. Settings sind Zukunftswerte, keine historischen Erwartungen.","Saved strength and goal level of both models appear beside actual results. Settings are future inputs, not historical expectations."),
              t("Should Goals messen unsere Leistung anhand der Spielstatistiken. Fehlende Daten bleiben sichtbar. Externe xGoals sind eine andere, optional verfügbare Quelle.","Should Goals measure performance using match statistics. Missing data stay visible. External xGoals are a different, optional source."),
              t("Die Quelle schaltet zwischen historischen Erwartungen beider Modelle, Ergebnissen und Markt um. Für genaue Anpassungen im Team dieselben Spiele vergleichen.","Source switches between historical expectations of both models, actual results and market. Team adjustments compare the same matches."),
              t("Ein Team genügt für die Rundtour. Dort sind Verstehen und bewusstes Anpassen direkt anwählbar. Vorschläge werden erst nach Vorschau und ausdrücklichem Speichern aktiv.","One team is enough for the tour. Understanding and deliberate editing are directly selectable. Suggestions activate only after preview and explicit saving.")
            ][orientation]}</p>
            {data&&<label>{t("Team für die Rundtour","Team for the tour")} <select aria-label={t("Team für die Rundtour","Team for the tour")} value="" onChange={e=>{if(e.target.value)setTeamIds([Number(e.target.value)]);}}><option value="">{t("Mannschaft wählen","Choose team")}</option>{rows.map(r=><option key={r.teamId} value={r.teamId}>{r.name||r.teamId}</option>)}</select></label>}
            {data&&rows.length>0&&<Button onClick={()=>setTeamIds([rows[0].teamId])}>{t("Beispielteam erkunden","Explore example team")}: {rows[0].name}</Button>}
            {!startLeague&&!leagueId&&<Alert severity="warning">{t("Bundesliga ist im geladenen Ligakatalog nicht verfügbar.","Bundesliga is unavailable in the loaded league catalogue.")}</Alert>}
          </section>}
          <section style={{"--tl-entry-background":`url(${process.env.PUBLIC_URL || ""}/betty-start-bg.png)`}} className={`tl-league-entry ${leagueId?'tl-league-entry-selected':''}`}>
            <div className="tl-league-entry-copy">
              <h2>{t("Welche Liga möchtest du untersuchen?","Which league would you like to explore?")}</h2>
              <p>{t("Wähle zuerst eine Liga. Danach kannst du Mannschaften vergleichen, ihre Werte prüfen und Änderungen als Vorschau berechnen.","Choose a league first. Then compare teams, inspect their values and preview changes.")}</p>

            <LeagueChoice grouped={exploration} disabled={catalogLoading}
              leagues={
                leagues.length ? leagues : initialLeague ? [initialLeague] : []
              }
              value={leagueId}
              label={t("Liga", "League")}
              placeholder={t("Bitte wählen", "Select")}
              onChange={(value) => {
                const selected =
                  leagues.find((l) => Number(l.id) === Number(value)) ||
                  initialLeague;
                setData(null);setSeasons([]);setOrientation(0);
                setLeagueId(value);
                setSeasonId(selected?.current_season_id || "");
              }}
            />

              {catalogLoading&&<p role="status">{t("Ligaauswahl wird geladen …","Loading leagues …")}</p>}
              {catalogError&&<Alert severity="error" action={<Button onClick={()=>setCatalogRetry(n=>n+1)}>{t("Erneut laden","Retry")}</Button>}>{catalogError === "PROVIDER_RATE_LIMIT" ? t("Der Datenabruf ist wegen eines Anbieterlimits vorübergehend pausiert. Für diese Auswahl ist noch kein gespeicherter Ligakatalog verfügbar.","Data requests are temporarily paused due to a provider limit. No saved league directory is available for this selection yet.") : t("Die Ligen konnten nicht geladen werden. Bitte erneut versuchen.","Could not load leagues. Please try again.")}</Alert>}
              {catalogCached&&<p role="status">{t("Ligaauswahl aus dem gespeicherten Katalog. Der Datenanbieter ist vorübergehend ausgelastet.","League selection uses the saved directory while the data provider is temporarily rate limited.")}</p>}
            </div>
            {!leagueId&&<div className="tl-entry-match" aria-label={t("Illustration eines Fußballspiels","Football match illustration")}>
              <span>{t("Ein Blick hinter das Ergebnis · Beispiel","Beyond the score · Example")}</span>
              <strong>68′ · 2 − 1</strong>
              {[['Ecken','Corners','6 − 3',70],['Torschüsse','Shots','12 − 8',60],['Flanken','Crosses','18 − 11',62]].map(([de,eng,value,width])=><div key={eng}><span>{t(de,eng)}</span><b>{value}</b><i style={{'--stat-width':`${width}%`}}/></div>)}
            </div>}
          </section>
          {leagueId&&<>
          <div className="tl-controls">
            <label>
              {t("Saison", "Season")}{" "}
              <select
                value={seasonId}
                onChange={(e) => setSeasonId(e.target.value)}
              >
                {!seasons.length && seasonId && (
                  <option value={seasonId}>{seasonId}</option>
                )}
                {seasons.map((s) => (
                  <option key={s.season_id} value={s.season_id}>
                    {s.season_name || s.name || s.season_id}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {t("Spielort", "Venue")}{" "}
              <select value={venue} onChange={(e) => setVenue(e.target.value)}>
                {[
                  ["overall", t("Gesamt", "All")],
                  ["home", t("Heim", "Home")],
                  ["away", t("Auswärts", "Away")],
                ].map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {t("Sortierung", "Sort")}{" "}
              <select value={sort} onChange={(e) => setSort(e.target.value)}>
                {[
                  ["position", t("Tabellenplatz", "Position")],
                  ["result", t("Stärke · Ergebnis", "Strength · results")],
                  [
                    "statistics",
                    t("Stärke · Statistik", "Strength · statistics"),
                  ],
                  ["points", t("Punkte", "Points")],
                  ["quotient", t("Reales Torverhältnis", "Actual goal ratio")],
                  ...extraColumns.map(c=>[c.key,c.label]),
                ].map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {t("Quelle", "Source")}{" "}
              <select
                value={source}
                onChange={(e) => setSource(e.target.value)}
              >
                {[
                  ["real", t("Reale Ergebnisse", "Actual results")],
                  [
                    "result",
                    t("Ergebnismodell · erwartet", "Results model · expected"),
                  ],
                  [
                    "statistics",
                    t(
                      "Statistikmodell · erwartet",
                      "Statistics model · expected"
                    ),
                  ],
                  ["market", t("Marktrückrechnung", "Market inversion")],
                  ["should", "Should Goals"],
                  ["previous", t("Vorsaison", "Previous season")],
                ].map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {t("Zeitraum", "Period")}{" "}
              <select
                disabled={source === "previous"}
                value={recent}
                onChange={(e) => setRecent(Number(e.target.value))}
              >
                <option value={0}>{t("Saison", "Season")}</option>
                {[4, 5, 6, 7, 8].map((n) => (
                  <option key={n} value={n}>
                    {t("Letzte", "Last")} {n}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <input
                type="checkbox"
                checked={perGame}
                onChange={(e) => setPerGame(e.target.checked)}
              />
              {t("Pro Spiel", "Per match")}
            </label>
            <Button
              onClick={() => {
                setColumns([...baseColumns, ...statisticColumns]);
                setShowStatistics(false);
                setAllSources(false);
              }}
            >
              {t("Kompakt", "Compact")}
            </Button>
            <Button
              onClick={() => {
                setColumns(Object.keys(labels));
                setAllSources(true);
                setShowStatistics(true);
              }}
            >
              {t("Alles anzeigen", "Show all")}
            </Button>
            <Button
              variant="outlined"
              aria-pressed={showStatistics}
              onClick={() => setShowStatistics((v) => !v)}
            >
              {showStatistics
                ? t("Statistik ausblenden", "Hide statistics")
                : t("Statistik anzeigen", "Show statistics")}
            </Button>
            <Button onClick={saveView}>
              {t(
                "Ansicht auf diesem Gerät speichern",
                "Save view on this device"
              )}
            </Button>
            <Button onClick={loadView}>
              {t("Ansicht laden", "Load view")}
            </Button>
          </div>
          <div className="tl-controls">
            <label>{t("Fragestellung", "Question")}
              <select defaultValue="" onChange={e=>{
                const key=e.target.value;
                if (!key) return;
                setAllSources(false);
                setSource("real");
                setColumns(key==="table" ? baseColumns : ["played","goals","points","quotient","goalAverage"]);
                setShowStatistics(key!=="table");
                setSort(key==="table" ? "position" : key);
              }}>
                <option value="">{t("Eigene Spaltenauswahl", "Custom columns")}</option>
                <option value="table">{t("Wer steht nach Punkten und Toren vorn?", "Who leads on points and goals?")}</option>
                <option value="result">{t("Wen sieht das Ergebnismodell vorn?", "Who leads in the results model?")}</option>
                <option value="statistics">{t("Wen sieht das Statistikmodell vorn?", "Who leads in the statistics model?")}</option>
                <option value="quotient">{t("Wer hat das beste reale Torverhältnis?", "Who has the best actual goal ratio?")}</option>
              </select>
            </label>
          </div>
          <details>
            <summary>{t("Spalten auswählen", "Choose columns")}</summary>
            <input
              aria-label={t("Spalten suchen", "Search columns")}
              placeholder={t("Kennzahl suchen …", "Search metric …")}
              value={columnSearch}
              onChange={(e) => setColumnSearch(e.target.value)}
            />
            <div className="tl-column-options">
              {Object.entries(labels)
                .filter(([, l]) =>
                  l.toLowerCase().includes(columnSearch.toLowerCase())
                )
                .map(([k, l]) => (
                  <label key={k}>
                    <input
                      type="checkbox"
                      checked={columns.includes(k)}
                      onChange={(e) => {
                        if (e.target.checked && statisticColumns.includes(k))
                          setShowStatistics(true);
                        setColumns(
                          e.target.checked
                            ? [...columns, k]
                            : columns.filter((c) => c !== k)
                        );
                      }}
                    />
                    {l}{" "}
                  </label>
                ))}
            </div>
          </details>
          </>}
          {error && <Alert severity="error">{error}</Alert>}
          {busy && (
            <CircularProgress
              aria-label={t("Liga wird geladen", "Loading league")}
            />
          )}
          {data && (
            <>
              <p>
                {league?.name} ·{" "}
                {data.season?.name ||
                  seasons.find((s) => s.season_id === Number(seasonId))
                    ?.season_name}{" "}
                ·{" "}
                {t(
                  "Ausgewählte Bilanzquelle; Modellwerte aus aktuellen persönlichen Settings. — bedeutet fehlend oder nicht definiert (z. B. Quotient bei null Gegentoren).",
                  "Selected record source; model values from current personal settings. — means missing or undefined (e.g. a ratio with zero goals against)."
                )}
              </p>
              {getUserAccessLevel(user) === "admin" && <XGoalsMigration key={`${leagueId}:${seasonId}`} {...{data,league,seasonId,request,en}} onSaved={()=>setRefresh(x=>x+1)} />}
              <LeagueReassessment {...{data,league,seasonId,request,en,mode}} onSaved={()=>setRefresh(x=>x+1)} />
              <WorkspaceStandingsTable goalSeparator={exploration?":":undefined}
                {...{
                  rows,
                  data,
                  visibleColumns,
                  visibleMetrics,
                  showStatistics,
                  groups,
                  columnClass,
                  digits,
                  labels,
                  rulesFor,
                  fmt,
                  t,
                  en,
                  extraColumns,
                  perGame,
                }}
                onTeam={(id) => {
                  savedScroll.current = scroll.current?.scrollTop || 0;
                  setTeamIds([id]);
                }}
              />
              <p className="tl-help" role="status">{normalization === true
                ? t("Normierung geprüft: Heim-Offensive = Auswärts-Defensive und Auswärts-Offensive = Heim-Defensive für Tore, Einzelstatistiken und Conversion (ungerundete gespeicherte Werte).", "Normalization checked: home attack = away defence and away attack = home defence for goals, statistics and conversion (unrounded saved values).")
                : normalization === false ? t("Normierung nicht erfüllt oder Daten unvollständig. Vor dem Speichern die normierte Liga-Vorschau prüfen.", "Normalization failed or data incomplete. Review the normalized league preview before saving.") : t("Normierung nicht prüfbar: vollständige Modellwerte fehlen.", "Normalization unavailable: complete model values missing.")}</p>
              {showStatistics && (
                <p className="tl-help">
                  {t(
                    "Quotient = Tore / Gegentore; Toreschnitt = (Tore + Gegentore) / Spiele. Der erspielte Quotient berücksichtigt keine Gegnerstärke. Gespeicherte Settings gelten für zukünftige Berechnungen.",
                    "Ratio = goals scored / conceded; goals/game = (scored + conceded) / matches. The observed ratio is not adjusted for opponent strength. Saved settings apply to future calculations."
                  )}
                </p>
              )}
              {(data.transitions || []).length > 0 && (
                <p className="tl-help">
                  {t(
                    "↑ Aufsteiger · ↓ Absteiger aus der Vorsaison. Farbige Platzmarkierungen gelten für die laufende Saison.",
                    "↑ Promoted · ↓ Relegated from the previous season. Coloured position markers refer to the current season."
                  )}
                </p>
              )}
              {allSources && showStatistics && (
                <LeagueComparisonMatrix
                  data={data}
                  rows={rows}
                  venue={venue}
                  recent={recent}
                  perGame={perGame}
                  en={en}
                />
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
      {teamIds && (
        <TeamAnalysisDialog
          workspace
          exploration={exploration}
          guideSession={guideSession.current}
          onStart={onStart}
          league={league || initialLeague}
          seasonId={seasonId}
          teamIds={teamIds}
          fixture={fixture}
          request={request}
          onSaved={() => setRefresh((x) => x + 1)}
          onClose={() => setTeamIds(null)}
        />
      )}
    </ThemeProvider>
  );
}
